import React, { useState, useEffect, useMemo } from "react";
import { ScrollView, View, StyleSheet, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { Menu } from "@/components/Menu";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import { useLocalSearchParams, router } from "expo-router";
import { getEntityPermissions } from "@/lib/permissions";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { useSettingsSearch } from "@/contexts/SettingsSearchContext";

type DressCode = {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  meta_data: Record<string, any>;
  created_at: string;
};

export default function DressCodesPage() {
  const { colors } = useTheme();
  const { tenantId, userRoles } = useAuth();
  const permissions = getEntityPermissions(userRoles, "dress_codes");
  const params = useLocalSearchParams<{ create?: string }>();

  const [dressCodes, setDressCodes] = useState<DressCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { searchValue } = useSettingsSearch("dress_codes");

  const filteredDressCodes = useMemo(() => {
    if (!searchValue.trim()) return dressCodes;
    const search = searchValue.toLowerCase();
    return dressCodes.filter(
      (item) => item.name?.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search),
    );
  }, [dressCodes, searchValue]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{ name: string; description: string }>({
    name: "",
    description: "",
  });

  const loadDressCodes = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .schema("lists")
        .from("dress_codes")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setDressCodes(data || []);
    } catch (err: any) {
      setError(err.message || "Failed to load dress codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDressCodes();
  }, []);

  // Watch for create param changes
  useEffect(() => {
    if (params.create === "true") {
      setShowForm(true);
      setEditingId(null);
      setFormData({ name: "", description: "" });
    }
  }, [params.create]);

  // Menu actions
  const handleEdit = (dressCode: DressCode) => {
    setEditingId(dressCode.id);
    setFormData({ name: dressCode.name, description: dressCode.description || "" });
    setShowForm(true);
    setError(null);
  };

  const handleDuplicate = (dressCode: DressCode) => {
    setEditingId(null);
    setFormData({ name: dressCode.name, description: dressCode.description || "" });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this dress code?")) return;

    try {
      const { error } = await supabase.schema("lists").from("dress_codes").delete().eq("id", id);

      if (error) throw error;
      setDressCodes(dressCodes.filter((dc) => dc.id !== id));
    } catch (err: any) {
      setError(err.message || "Failed to delete dress code");
    }
  };

  // Form submit handler
  const handleSubmit = async () => {
    if (!tenantId || !formData.name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      if (editingId) {
        const { data, error } = await supabase
          .schema("lists")
          .from("dress_codes")
          .update({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
          })
          .eq("id", editingId)
          .select()
          .single();

        if (error) throw error;
        setDressCodes(dressCodes.map((dc) => (dc.id === editingId ? data : dc)));
      } else {
        const { data, error } = await supabase
          .schema("lists")
          .from("dress_codes")
          .insert([
            {
              id: crypto.randomUUID(),
              name: formData.name.trim(),
              description: formData.description.trim() || null,
              tenant_id: tenantId,
              meta_data: {},
            },
          ])
          .select()
          .single();

        if (error) throw error;
        setDressCodes([...dressCodes, data]);
      }

      setFormData({ name: "", description: "" });
      setShowForm(false);
      setEditingId(null);
      setError(null);
      router.setParams({ create: undefined });
    } catch (err: any) {
      setError(err.message || (editingId ? "Failed to update dress code" : "Failed to add dress code"));
    }
  };

  const handleCancel = () => {
    setFormData({ name: "", description: "" });
    setShowForm(false);
    setEditingId(null);
    setError(null);
    router.setParams({ create: undefined });
  };

  const dressCodeColor = colors.purple.border;
  const dressCodeBackground = colors.purple.background;

  if (!permissions.read) {
    return <NoPermissionScreen entityName="dress codes" />;
  }

  return (
    <ScrollView style={styles.container}>
      <View>
        {loading ? (
          <ThemedText style={styles.centered}>Loading dress codes...</ThemedText>
        ) : error ? (
          <View style={styles.errorContainer}>
            <ThemedText type="defaultSemiBold" style={{ color: colors.red.text }}>
              {error}
            </ThemedText>
            <Button text="Retry" icon="refresh" style={{ marginTop: 8 }} onPress={loadDressCodes} />
          </View>
        ) : (
          <View style={styles.cardsContainer}>
            {permissions.create && showForm && !editingId && (
              <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.editForm}>
                  <TextInput
                    style={[
                      styles.input,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Name *"
                    placeholderTextColor={colors.placeholder}
                    value={formData.name}
                    onChangeText={(text) => setFormData({ ...formData, name: text })}
                    autoFocus
                  />
                  <TextInput
                    style={[
                      styles.input,
                      styles.descriptionInput,
                      { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                    ]}
                    placeholder="Description (optional)"
                    placeholderTextColor={colors.placeholder}
                    value={formData.description}
                    onChangeText={(text) => setFormData({ ...formData, description: text })}
                    multiline
                  />
                  <View style={styles.editActions}>
                    <Button
                      text="Cancel"
                      style={[styles.smallBtn, { borderColor: colors.border }]}
                      textStyle={{ fontSize: 13 }}
                      onPress={handleCancel}
                    />
                    <Button
                      text="Add"
                      icon="plus"
                      iconPosition="right"
                      style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                      textStyle={{ fontSize: 13, color: "white" }}
                      onPress={handleSubmit}
                      enabled={!!formData.name.trim()}
                    />
                  </View>
                </View>
              </View>
            )}

            {filteredDressCodes.map((dressCode) => {
              const isEditing = editingId === dressCode.id;

              return (
                <View
                  key={dressCode.id}
                  style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}
                >
                  {isEditing ? (
                    <View style={styles.editForm}>
                      <TextInput
                        style={[
                          styles.input,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Name *"
                        placeholderTextColor={colors.placeholder}
                        value={formData.name}
                        onChangeText={(text) => setFormData({ ...formData, name: text })}
                        autoFocus
                      />
                      <TextInput
                        style={[
                          styles.input,
                          styles.descriptionInput,
                          {
                            borderColor: colors.border,
                            color: colors.text,
                            backgroundColor: colors.backgroundSecondary,
                          },
                        ]}
                        placeholder="Description (optional)"
                        placeholderTextColor={colors.placeholder}
                        value={formData.description}
                        onChangeText={(text) => setFormData({ ...formData, description: text })}
                        multiline
                      />
                      <View style={styles.editActions}>
                        <Button
                          text="Cancel"
                          style={[styles.smallBtn, { borderColor: colors.border }]}
                          textStyle={{ fontSize: 13 }}
                          onPress={handleCancel}
                        />
                        <Button
                          text="Update"
                          icon="check"
                          iconPosition="right"
                          style={[styles.smallBtn, { backgroundColor: colors.primary }]}
                          textStyle={{ fontSize: 13, color: "white" }}
                          onPress={handleSubmit}
                          enabled={!!formData.name.trim()}
                        />
                      </View>
                    </View>
                  ) : (
                    <View style={styles.itemContent}>
                      <View style={styles.itemInfo}>
                        <View style={styles.itemHeader}>
                          <ThemedText type="defaultSemiBold">{dressCode.name}</ThemedText>
                          <Menu
                            buttonStyle={{
                              marginLeft: "auto",
                              alignSelf: "flex-start",
                              borderWidth: 0,
                              backgroundColor: "transparent",
                            }}
                            hideArchive={true}
                            canEdit={permissions.update}
                            canDuplicate={permissions.create}
                            canDelete={permissions.delete}
                            onEdit={() => handleEdit(dressCode)}
                            onDuplicate={() => handleDuplicate(dressCode)}
                            onDelete={() => handleDelete(dressCode.id)}
                          />
                        </View>
                        {dressCode.description && (
                          <ThemedText style={[styles.description, { color: colors.textSecondary }]}>
                            {dressCode.description}
                          </ThemedText>
                        )}
                      </View>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 16,
  },
  cardsContainer: {
    gap: 12,
  },
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    overflow: "hidden",
  },
  itemContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  itemInfo: {
    flex: 1,
    gap: 4,
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
  },
  description: {
    fontSize: 13,
  },
  editForm: {
    padding: 16,
    gap: 12,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  descriptionInput: {
    height: 60,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  editActions: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
  },
  smallBtn: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  centered: {
    textAlign: "center",
    paddingVertical: 32,
    opacity: 0.7,
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: 32,
  },
});
